import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Printer, ArrowLeft } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatINR, formatDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

/* ───────────────────────────────────────────────────────────────
   REEDOS Premium Tax Invoice — print stylesheet
   - Mirrors the proposal print system (tokens, eyebrows, sections)
   - Adds invoice-specific Hero Callout (Amount Due / Pay By)
   ─────────────────────────────────────────────────────────────── */
const printStyles = `
:root {
  --reedos-blue: #1A237E;
  --reedos-orange: #E8472C;
  --ink: #111827;
  --muted: #4B5563;
  --light: #6B7280;
  --rule: #E5E7EB;
  --rule-strong: #D1D5DB;
  --table-header: #F8FAFC;
  --card-bg: #FAFAFA;
  --warn: #B91C1C;
  --warn-bg: #FEF2F2;
}

* { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }

@page {
  size: A4;
  margin: 16mm 14mm 24mm 14mm;
  @bottom-left {
    content: "REEDOS Software Solutions Private Limited  ·  GSTIN 27AAJCR1173P1ZT  ·  TAX INVOICE";
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 7pt;
    color: #6B7280;
    letter-spacing: 0.02em;
  }
  @bottom-right {
    content: "Page " counter(page) " of " counter(pages);
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 7pt;
    color: #6B7280;
    letter-spacing: 0.06em;
  }
}

@media print {
  [data-testid="app-layout"] > aside,
  [data-testid="app-header"],
  [data-testid="app-sidebar"],
  aside,
  nav[aria-label="Sidebar"],
  /* 2026-02-10 REEDOS branding compliance: removed legacy
     third-party-badge hide-rules; the loader that injected those
     elements no longer ships in any build. */
  [data-sonner-toaster],
  [cmdk-overlay],
  [cmdk-root],
  .no-print { display: none !important; }

  body, html { background: white !important; margin: 0 !important; padding: 0 !important; }
  [data-testid="app-layout"] { display: block !important; }
  [data-testid="app-layout"] > main { display: block !important; }

  .print-shell {
    box-shadow: none !important;
    border: none !important;
    margin: 0 !important;
    padding: 0 !important;
    max-width: none !important;
    width: 100% !important;
    background: white !important;
  }
  h1, h2 { page-break-after: avoid; break-after: avoid; }
  table, .items-card, .pay-banner { page-break-inside: avoid; break-inside: avoid; }
}

.print-shell {
  max-width: 820px;
  margin: 24px auto;
  background: white;
  padding: 18mm 16mm 14mm 16mm;
  border: 1px solid var(--rule);
  font-family: 'Inter', system-ui, sans-serif;
  color: var(--ink);
  font-size: 10pt;
  line-height: 1.55;
  font-feature-settings: 'ss01', 'cv11';
}
.print-shell h1, .print-shell h2, .print-shell h3 {
  font-family: 'Satoshi', 'Inter', sans-serif;
  color: var(--ink);
  margin: 0;
}

/* Top header bar */
.print-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-bottom: 8mm;
  border-bottom: 1px solid var(--rule);
  margin-bottom: 10mm;
}
.print-header .logo { height: 38px; width: auto; display: block; }
.print-header .doc-meta { text-align: right; font-size: 8pt; color: var(--light); line-height: 1.6; }
.print-header .doc-meta .tag {
  display: inline-block;
  padding: 2px 10px;
  background: var(--reedos-blue);
  color: #fff;
  font-size: 8pt;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  border-radius: 2px;
  margin-bottom: 4px;
}
.print-header .doc-meta .num { font-family: 'Satoshi', sans-serif; font-size: 11pt; color: var(--ink); font-weight: 700; letter-spacing: 0.01em; margin-bottom: 1mm; }
.print-header .doc-meta .label {
  display: inline-block;
  margin-top: 1mm;
  padding: 1.5px 8px;
  background: #EEF2FF;
  color: #3730A3;
  border: 0.4pt solid #C7D2FE;
  border-radius: 2px;
  font-size: 8pt;
  font-weight: 600;
  letter-spacing: 0.01em;
  max-width: 62mm;
  white-space: normal;
  line-height: 1.35;
}
.print-header .doc-meta .copy { font-size: 7.5pt; color: var(--light); letter-spacing: 0.14em; text-transform: uppercase; font-weight: 600; margin-top: 1mm; }

/* Bill From — supplier (REEDOS) block */
.bill-from {
  margin-bottom: 6mm;
  padding: 4mm 5mm;
  background: var(--card-bg);
  border: 0.6pt solid var(--rule);
  border-radius: 2px;
}
.bill-from .eyebrow {
  font-size: 7.5pt; font-weight: 700; letter-spacing: 0.22em;
  text-transform: uppercase; color: var(--reedos-blue);
  margin-bottom: 2mm;
}
.bill-from .co-name {
  font-family: 'Satoshi', sans-serif;
  font-size: 12pt; font-weight: 700; color: var(--ink);
  letter-spacing: -0.01em; line-height: 1.2;
  margin-bottom: 1.5mm;
}
.bill-from .co-address { font-size: 8.5pt; color: var(--muted); line-height: 1.5; margin-bottom: 1.5mm; }
.bill-from .co-ids { font-size: 8pt; color: var(--muted); line-height: 1.6; }
.bill-from .co-ids .label { color: var(--light); font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; font-size: 7pt; margin-right: 3px; }
.bill-from .co-ids .mono { font-family: 'Satoshi', sans-serif; font-weight: 600; color: var(--ink); }
.bill-from .co-ids .sep { margin: 0 6px; color: var(--rule-strong); }

/* Hero — Bill To */
.bill-hero { margin-bottom: 8mm; }
.bill-hero .eyebrow {
  font-size: 8pt; font-weight: 600; letter-spacing: 0.18em;
  text-transform: uppercase; color: var(--reedos-orange);
  margin-bottom: 3mm;
}
.bill-hero .client-name {
  font-family: 'Satoshi', sans-serif;
  font-size: 22pt; font-weight: 700;
  color: var(--ink); letter-spacing: -0.02em;
  line-height: 1.15;
  margin-bottom: 3mm;
}
.bill-hero .client-meta { font-size: 9pt; color: var(--muted); line-height: 1.6; max-width: 110mm; }
.bill-hero .client-meta .label { color: var(--light); font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; font-size: 7.5pt; margin-right: 4px; }
.bill-hero .client-meta .mono { font-family: 'Satoshi', sans-serif; font-weight: 600; }
.bill-hero .locked-stamp {
  margin-top: 3mm;
  font-size: 7.5pt;
  color: #047857;
  background: #ECFDF5;
  padding: 1.2mm 2.5mm;
  border-radius: 2px;
  display: inline-block;
  letter-spacing: 0.02em;
  font-weight: 500;
}

/* Pay-banner — the wow callout */
.pay-banner {
  display: grid;
  grid-template-columns: 1fr 1fr;
  border: 1px solid var(--rule);
  border-top: 3px solid var(--reedos-blue);
  border-radius: 4px;
  margin-bottom: 12mm;
}
.pay-banner .cell {
  padding: 6mm 7mm;
  border-right: 1px solid var(--rule);
}
.pay-banner .cell:last-child { border-right: 0; }
.pay-banner .lh {
  font-size: 7.5pt; font-weight: 700; letter-spacing: 0.18em;
  text-transform: uppercase; color: var(--light);
  margin-bottom: 2mm;
}
.pay-banner .amount {
  font-family: 'Satoshi', sans-serif;
  font-size: 22pt; font-weight: 700;
  color: var(--reedos-blue); letter-spacing: -0.02em;
  font-feature-settings: 'tnum'; line-height: 1.1;
}
.pay-banner .amount.paid { color: #047857; }
.pay-banner .amount-sub {
  font-size: 8pt; color: var(--light); margin-top: 2mm; letter-spacing: 0.02em;
}
.pay-banner .due-date {
  font-family: 'Satoshi', sans-serif;
  font-size: 16pt; font-weight: 700;
  color: var(--ink); letter-spacing: -0.01em; line-height: 1.15;
}
.pay-banner .due-date.overdue { color: var(--warn); }
.pay-banner .terms-badge {
  display: inline-block;
  margin-top: 3mm;
  padding: 2px 8px;
  border-radius: 2px;
  background: var(--card-bg);
  font-size: 8pt; font-weight: 600; color: var(--ink);
  letter-spacing: 0.06em; text-transform: uppercase;
}
.pay-banner .overdue-pill {
  display: inline-block;
  margin-top: 3mm;
  padding: 2px 8px;
  border-radius: 2px;
  background: var(--warn-bg);
  color: var(--warn);
  font-size: 8pt; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase;
}
.pay-banner .paid-pill {
  display: inline-block;
  margin-top: 3mm;
  padding: 2px 8px;
  border-radius: 2px;
  background: #ECFDF5;
  color: #047857;
  font-size: 8pt; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase;
}

/* Eyebrow + section title */
.eyebrow {
  font-family: 'Inter', sans-serif;
  font-size: 8pt; font-weight: 600; letter-spacing: 0.16em;
  text-transform: uppercase; color: var(--reedos-orange);
  display: inline-flex; align-items: center; gap: 8px;
}
.eyebrow .num {
  font-family: 'Satoshi', sans-serif;
  color: var(--reedos-blue); font-weight: 700;
  font-size: 9pt; letter-spacing: 0.04em;
}
.eyebrow .num::after { content: " /"; color: var(--rule-strong); margin-left: 2px; font-weight: 400; }

.section-title { margin-bottom: 6mm; }
.section-title h2 { font-size: 14pt; font-weight: 700; color: var(--ink); letter-spacing: -0.005em; margin-top: 2mm; }
.section-title .accent-rule { width: 22mm; height: 2px; background: var(--reedos-orange); margin-top: 4mm; }
.section { margin-bottom: 14mm; }

/* Invoice meta strip — top reference for invoice/date/po/billing-period */
.invoice-meta-strip {
  border: 1px solid var(--rule);
  border-radius: 4px;
  padding: 4mm 5mm;
  margin-bottom: 8mm;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 5mm;
}
.invoice-meta-strip .item .lh { font-size: 7.5pt; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; color: var(--light); margin-bottom: 1mm; }
.invoice-meta-strip .item .val { font-size: 10pt; font-weight: 600; color: var(--ink); }
.invoice-meta-strip .item .val.mono { font-family: 'Satoshi', sans-serif; letter-spacing: 0.01em; }

/* Items card */
.items-card {
  border: 1px solid var(--rule);
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}
.items {
  width: 100%; border-collapse: collapse;
  font-size: 9pt;
}
.items thead th {
  background: var(--table-header);
  color: var(--ink);
  font-family: 'Inter', sans-serif;
  font-weight: 600; font-size: 8pt;
  letter-spacing: 0.04em; text-transform: uppercase;
  padding: 3mm 3.5mm;
  border-bottom: 1px solid var(--rule);
  text-align: right;
}
.items thead th.text-left { text-align: left; }
.items tbody td {
  padding: 3mm 3.5mm;
  border-bottom: 1px solid #F3F4F6;
  vertical-align: top;
  font-feature-settings: 'tnum';
  font-variant-numeric: tabular-nums;
  text-align: right;
}
.items tbody td.text-left { text-align: left; }
.items tbody tr:last-child td { border-bottom: 0; }
.items .num-col { width: 9mm; color: var(--light); }
.items .desc { font-weight: 600; color: var(--ink); font-size: 9.5pt; }
.items .desc-sub { font-size: 8pt; color: var(--light); margin-top: 1px; }
/* Iter 29: AMC subscription / service-period sub-lines, styled subtly
 * beneath the product name to mirror the operational-invoice convention.
 * Smaller, lighter, indented — present but not overpowering. */
.items .amc-charge-label { font-size: 8pt; color: var(--muted); font-weight: 500; margin-top: 2px; padding-left: 1mm; letter-spacing: 0.005em; }
.items .amc-period { font-size: 8pt; color: var(--light); font-weight: 400; margin-top: 0.5px; padding-left: 1mm; letter-spacing: 0.005em; }
.items .amc-period .label { color: var(--muted); font-weight: 500; margin-right: 1mm; }
.items .figure { font-weight: 500; color: var(--ink); white-space: nowrap; }
.items .figure-em { font-weight: 700; }
.items .gst-pct { display: block; font-size: 7.5pt; color: var(--light); margin-top: 1px; font-weight: 400; letter-spacing: 0.02em; }
.items tfoot td {
  background: var(--card-bg);
  border-top: 1px solid var(--rule);
  padding: 2.8mm 3.5mm;
  font-weight: 600; color: var(--ink); font-size: 9pt;
  font-feature-settings: 'tnum'; font-variant-numeric: tabular-nums;
  text-align: right;
}
.items tfoot tr.subtotal-row td { background: var(--card-bg); }
.items tfoot tr.grand-row td {
  background: var(--ink); color: #fff;
  border-top: 2px solid var(--reedos-blue);
  padding: 4.5mm 3.5mm;
  font-weight: 700; font-size: 11pt;
  letter-spacing: 0;
}
.items tfoot tr.grand-row td.label {
  letter-spacing: 0.18em; text-transform: uppercase; font-size: 9.5pt;
}
.items tfoot td.label { text-align: right; letter-spacing: 0.06em; text-transform: uppercase; font-size: 8.5pt; color: var(--ink); font-weight: 600; }

/* ── Iter 2026-02 · Invoice = Proposal-mirror UAT ───────────────────────
   Rows to mirror the proposal table structure (option-band, product
   header, module sub-lines, group subtotal). Class names and typography
   deliberately mirror ProposalPrint.jsx (Iter 97.15) so the client sees
   an identical presentation on proposal and invoice.  */
.items .option-band td {
  background: linear-gradient(180deg, #0F172A 0%, #1E293B 100%);
  color: #E2E8F0;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: 8.5pt;
  padding: 3mm 4mm;
  border: 0;
}
.items .option-band-desc td.option-description {
  background: #F8FAFC;
  border-bottom: 0.4pt solid var(--rule);
  padding: 2mm 4mm 3mm;
  color: var(--muted);
  font-size: 9pt;
  line-height: 1.55;
  font-style: italic;
}
.items .itemized-product-header td { padding-top: 3mm; padding-bottom: 1mm; border-top: 0.4pt solid var(--rule); background: #FAFBFC; }
.items .itemized-product-header td .product-name { font-weight: 700; color: var(--ink); font-size: 10pt; }
.items .itemized-product-header td .product-sub { font-size: 8.5pt; color: var(--muted); margin-top: 1px; }
.items .itemized-product-header td.figure { color: var(--light); font-weight: 400; }
.items .itemized-module-row td { border-top: none; padding-top: 2px; padding-bottom: 2px; }
.items .itemized-module-row .scope-module-line { font-size: 9.5pt; color: var(--ink); line-height: 1.5; padding-left: 6mm; }
.items .option-subtotal td {
  background: #F1F5F9;
  border-top: 0.4pt solid var(--rule);
  font-weight: 600;
  font-size: 9pt;
  color: var(--ink);
  padding: 2.5mm 4mm;
  letter-spacing: 0.03em;
}
.items .mirror-consolidated-note td { border-top: none; }

/* Amount-in-words callout */
.words-callout {
  margin-top: 4mm;
  padding: 3.5mm 5mm;
  border-left: 2px solid var(--reedos-orange);
  background: #FFF7F4;
  font-size: 9pt;
  line-height: 1.6;
  color: var(--ink);
}
.words-callout .lh { font-size: 7.5pt; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; color: var(--reedos-orange); margin-right: 6px; }

/* Bank / Payment block */
.payment-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 6mm;
}
.payment-card {
  border: 1px solid var(--rule);
  border-top: 3px solid var(--reedos-blue);
  border-radius: 4px;
  padding: 5mm 6mm;
}
.payment-card .head { font-family: 'Satoshi', sans-serif; font-size: 9.5pt; font-weight: 700; color: var(--ink); margin-bottom: 3mm; letter-spacing: -0.005em; }
.payment-card .row {
  display: grid; grid-template-columns: 28mm 1fr;
  padding: 1.5mm 0; border-bottom: 1px solid #F3F4F6;
  font-size: 9pt;
}
.payment-card .row:last-child { border-bottom: 0; }
.payment-card .row .lh { font-size: 7.5pt; color: var(--light); font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; padding-top: 1px; }
.payment-card .row .val { color: var(--ink); font-weight: 600; }
.payment-card .row .val.mono { font-family: 'Satoshi', sans-serif; letter-spacing: 0.01em; }
.payment-card .note {
  margin-top: 4mm;
  padding-top: 3mm;
  border-top: 1px dashed var(--rule);
  font-size: 8.5pt;
  color: var(--muted);
  line-height: 1.55;
}

/* Notes & declarations */
.declarations {
  list-style: decimal;
  padding-left: 6mm;
  font-size: 8.5pt; line-height: 1.7; color: var(--muted);
}
.declarations li { padding-left: 1mm; margin-bottom: 1.5mm; }
.declarations li::marker { color: var(--reedos-blue); font-weight: 700; }

/* Footer */
.doc-footer {
  margin-top: 14mm;
  padding-top: 4mm;
  border-top: 1px solid var(--rule);
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 8pt; color: var(--light);
  letter-spacing: 0.02em;
}
.doc-footer .org-line strong { color: var(--ink); font-weight: 700; }
.doc-footer .conf { letter-spacing: 0.18em; text-transform: uppercase; font-weight: 600; }
`;

function getOutstanding(invoice) {
    // Prefer enriched fields when present
    if (typeof invoice.outstanding === "number") return invoice.outstanding;
    const total = Number(invoice.total_amount || 0);
    const paid = Number(invoice.paid_amount || 0);
    const tds = Number(invoice.tds_amount || 0);
    const cn = Number(invoice.credit_note_total || 0);
    return Math.max(0, total - paid - tds - cn);
}

function daysBetween(fromIso, toIso) {
    if (!fromIso || !toIso) return null;
    const a = new Date(fromIso);
    const b = new Date(toIso);
    return Math.round((a.getTime() - b.getTime()) / 86400000);
}

/* ────────────────────────────────────────────────────────────────────
   Iter 2026-02 · Reusable render body — extracted from the route-based
   `InvoicePrint` page so the exact same PDF-quality layout can also be
   rendered inside the "Invoice Preview" modal on the PO detail page
   (before the operator commits to burning an invoice number).

   Contract:
     - `invoice` and `company` are pre-fetched by the caller
     - `isPreview` renders a subtle "PREVIEW" watermark and skips the
       route-only chrome (top nav / stand-alone page background)
   ──────────────────────────────────────────────────────────────────── */
export function InvoicePrintBody({ invoice, company, isPreview = false }) {
    const isIntra = useMemo(() => {
        if (!invoice) return false;
        // C.3 Commit 1 — gst_breakup may be null on suppressed (non-INR/non-GST)
        // invoices per C-1 remediation. Treat null as "no intra-state GST".
        const bk = invoice.gst_breakup;
        if (!bk) return false;
        return (bk.cgst || 0) > 0 || (bk.sgst || 0) > 0;
    }, [invoice]);

    // Iter 43 — Tax applicability. Snapshot frozen on the invoice at creation
    // time. When False (international / zero-rated client) the renderer:
    //   • hides Place of Supply from the meta strip
    //   • drops all GST rows from the footer summary
    //   • shows only Subtotal → Grand Total
    // Legacy invoices (pre-Iter 43) without the field default to true so the
    // existing domestic rendering is preserved.
    const gstApplicable = invoice ? (invoice.gst_applicable !== false) : true;

    // Iter 42 — Uniform GST detection for enterprise-friendly simplified
    // invoice rendering. The simplified presentation (Description · HSN · Amount
    // + consolidated footer) is the cleaner default; line-level GST visibility
    // automatically reappears when ANY of these conditions is true:
    //   • mixed GST rates across line items
    //   • an exempt/zero-rated line (gst_percent === 0)
    //   • an export / reverse-charge / non-standard flag on any line
    // Storage stays full-fidelity — only the rendered table is simplified.
    const uniformGst = useMemo(() => {
        if (!invoice?.line_items?.length) return false;
        // Iter 43: international / non-GST invoices always render the
        // simplified table (Subtotal → Grand Total only, no GST rows).
        if (invoice.gst_applicable === false) return true;
        const rates = new Set();
        let hasSpecialFlag = false;
        for (const li of invoice.line_items) {
            const r = Number(li.gst_percent ?? 0);
            if (r <= 0) return false;  // exempt / zero-rated → keep line GST visible
            rates.add(r);
            if (li.is_export || li.reverse_charge || li.tax_exempt) hasSpecialFlag = true;
        }
        return rates.size === 1 && !hasSpecialFlag;
    }, [invoice]);

    // C.3 Commit 1 — C-4 remediation. Suppress the HSN/SAC column when
    // ALL line items have a null/empty hsn_sac_code (the typical
    // international / non-INR invoice shape). INR invoices with at least
    // one populated HSN code keep rendering the column for backwards-compat.
    const suppressHsnColumn = useMemo(() => {
        if (!invoice?.line_items?.length) return false;
        return invoice.line_items.every((li) => !li.hsn_sac_code);
    }, [invoice]);

    // Iter 2026-02 · Invoice = Proposal-mirror UAT. Detect whether the
    // invoice carries the Builder structural snapshot (row_kind /
    // presentation_mode on the lines) — populated by the /invoice/
    // from-po path when the source PO was Builder-materialised.
    // Manual + legacy invoices leave these fields null and fall back
    // to the flat-row renderer.
    const builderMirror = useMemo(() => {
        if (!invoice?.line_items?.length) return null;
        const hasStructural = invoice.line_items.some((l) => !!l.row_kind || !!l.presentation_mode);
        if (!hasStructural) return null;
        // Presentation mode is on every line (frozen at PO time) — read
        // the first non-null value.
        const mode = (invoice.line_items.find((l) => l.presentation_mode) || {}).presentation_mode || "itemized";
        // Group by option_label. `__default__` covers lines without an
        // option (single-product proposals).
        const groups = new Map();
        for (const li of invoice.line_items) {
            const key = li.option_label || "__default__";
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(li);
        }
        const hasOptions = [...groups.keys()].some((k) => k !== "__default__");
        // Price visibility per mode — mirror ProposalPrint's Iter 97.15
        // logic exactly so the client sees identical presentation on
        // proposal and invoice.
        //   itemized     → per-line amounts visible, subtotals visible
        //   grouped      → module prices hidden, subtotals visible
        //   consolidated → all per-line amounts hidden, only grand total
        const showModulePrices = mode === "itemized";
        const showAtomicPrices = mode !== "consolidated";
        const showSubtotals = mode !== "consolidated";
        // Per-group Impl subtotal for the "Subtotal — {group}" footer
        // rows. Sums line.amount (which is the scaled Impl portion
        // already computed on the backend). Structural rows contribute
        // 0 so they don't inflate the subtotal.
        const perGroupSubtotal = new Map();
        for (const [label, lines] of groups.entries()) {
            const sum = lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
            perGroupSubtotal.set(label, sum);
        }
        return { groups, hasOptions, mode, showModulePrices, showAtomicPrices, showSubtotals, perGroupSubtotal };
    }, [invoice]);

    if (!invoice || !company) return null;

    const placeOfSupply = invoice.client?.state_code === company.state_code
        ? company.place_of_supply
        : invoice.client?.state_code || company.place_of_supply;

    const outstanding = getOutstanding(invoice);
    const fullyPaid = outstanding <= 0.01;
    const today = new Date().toISOString();
    // daysBetween(due, today) = due - today  →  positive when due is in the future, negative when overdue
    const daysToDue = daysBetween(invoice.due_date, today);
    const isOverdue = !fullyPaid && daysToDue !== null && daysToDue < 0;
    const dueLabel = fullyPaid
        ? "Settled"
        : isOverdue
            ? `${Math.abs(daysToDue)} day${Math.abs(daysToDue) === 1 ? "" : "s"} overdue`
            : (daysToDue !== null && daysToDue > 0 ? `Due in ${daysToDue} day${daysToDue === 1 ? "" : "s"}` : (daysToDue === 0 ? "Due today" : ""));

    const sectionEyebrow = (num, label) => (
        <div className="eyebrow"><span className="num">{num}</span>{label}</div>
    );

    return (
        <>
            <style>{printStyles}</style>
            {isPreview && (
                <div
                    aria-hidden="true"
                    className="pointer-events-none no-print"
                    style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        zIndex: 1,
                    }}
                    data-testid="invoice-preview-watermark"
                >
                    <div style={{
                        transform: "rotate(-24deg)",
                        fontFamily: "'Satoshi', 'Inter', sans-serif",
                        fontSize: "120pt",
                        fontWeight: 800,
                        letterSpacing: "0.08em",
                        color: "rgba(232, 71, 44, 0.08)",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                    }}>
                        PREVIEW
                    </div>
                </div>
            )}
            <div
                className="print-shell shadow-sm"
                data-testid={isPreview ? "invoice-preview-shell" : "invoice-print-shell"}
                style={{ position: "relative", zIndex: 2 }}
            >
                {/* Top header bar */}
                <div className="print-header">
                    <img src="/reedos-logo.png" alt="REEDOS" className="logo" />
                    <div className="doc-meta">
                        <div className="tag">Tax Invoice</div>
                        <div className="num">{invoice.invoice_number}</div>
                        <div>{formatDate(invoice.invoice_date)}</div>
                        {/* UAT 2026-02 — Operator-authored invoice label (e.g.
                            service period, milestone reference) must appear on
                            the printed PDF, not only as the in-app badge.
                            Placed under the invoice number so the reader
                            immediately sees what this invoice is for.  */}
                        {invoice.invoice_label && (
                            <div className="label" data-testid="invoice-print-label">
                                {invoice.invoice_label}
                            </div>
                        )}
                        <div className="copy">Original for Recipient</div>
                    </div>
                </div>

                {/* Bill-from — REEDOS supplier block (Rule 46 CGST compliance) */}
                <section className="bill-from" data-testid="invoice-bill-from">
                    <div className="eyebrow">Bill From</div>
                    <div className="co-name">{company.legal_name}</div>
                    {(company.registered_office || company.address) && (
                        <div className="co-address">
                            {company.registered_office || company.address}
                        </div>
                    )}
                    <div className="co-ids">
                        {company.gstin && <><span className="label">GSTIN</span><span className="mono">{company.gstin}</span></>}
                        {company.gstin && company.pan && <span className="sep">·</span>}
                        {company.pan && <><span className="label">PAN</span><span className="mono">{company.pan}</span></>}
                        {(company.gstin || company.pan) && company.state_code && <span className="sep">·</span>}
                        {company.state_code && <><span className="label">State</span><span className="mono">{company.state_code}</span></>}
                    </div>
                </section>

                {/* Bill-to hero */}
                <section className="bill-hero" data-testid="invoice-bill-to">
                    <div className="eyebrow">Bill To</div>
                    <div className="client-name">{invoice.client?.name || "Client name not set"}</div>
                    <div className="client-meta">
                        {invoice.client?.address && <div>{invoice.client.address}</div>}
                        <div style={{ marginTop: "1.5mm" }}>
                            {invoice.client?.gstin && <><span className="label">GSTIN</span><span className="mono">{invoice.client.gstin}</span></>}
                            {invoice.client?.gstin && invoice.client?.state_code && <span style={{ margin: "0 8px", color: "var(--rule-strong)" }}>·</span>}
                            {invoice.client?.state_code && <><span className="label">State</span><span className="mono">{invoice.client.state_code}</span></>}
                        </div>
                        {invoice.bill_to?.snapshot_at && (
                            <div className="locked-stamp" data-testid="bill-to-locked-stamp">
                                Bill-to fixed as on {formatDate(invoice.bill_to.snapshot_at)} — historical record, not affected by later client edits.
                            </div>
                        )}
                    </div>
                </section>

                {/* Pay-banner — Amount Due / Pay By */}
                <div className="pay-banner" data-testid="invoice-pay-banner">
                    <div className="cell">
                        <div className="lh">{fullyPaid ? "Amount Settled" : "Amount Due"}</div>
                        <div className={`amount ${fullyPaid ? "paid" : ""}`}>{formatINR(fullyPaid ? invoice.total_amount : outstanding, invoice?.currency)}</div>
                        <div className="amount-sub">
                            {fullyPaid
                                ? "Fully settled. Thank you."
                                : <>Total invoice {formatINR(invoice.total_amount, invoice?.currency)}{(invoice.paid_amount || 0) > 0 ? ` · paid ${formatINR(invoice.paid_amount, invoice?.currency)}` : ""}{(invoice.tds_amount || 0) > 0 ? ` · TDS ${formatINR(invoice.tds_amount, invoice?.currency)}` : ""}{(invoice.credit_note_total || 0) > 0 ? ` · credits ${formatINR(invoice.credit_note_total, invoice?.currency)}` : ""}</>
                            }
                        </div>
                    </div>
                    <div className="cell">
                        <div className="lh">Pay By</div>
                        <div className={`due-date ${isOverdue ? "overdue" : ""}`}>{formatDate(invoice.due_date)}</div>
                        {fullyPaid
                            ? <span className="paid-pill">Paid</span>
                            : isOverdue
                                ? <span className="overdue-pill">{dueLabel}</span>
                                : <span className="terms-badge">{invoice.payment_terms}{dueLabel ? ` · ${dueLabel}` : ""}</span>
                        }
                    </div>
                </div>

                {/* Invoice meta strip */}
                <div className="invoice-meta-strip" data-testid="invoice-meta-strip">
                    <div className="item">
                        <div className="lh">Invoice No.</div>
                        <div className="val mono">{invoice.invoice_number}</div>
                    </div>
                    {/* Iter 43: Place of Supply is hidden for international /
                        non-GST invoices — the Bill-To country block already
                        communicates destination context. */}
                    {gstApplicable && (
                        <div className="item">
                            <div className="lh">Place of Supply</div>
                            <div className="val">{placeOfSupply}</div>
                        </div>
                    )}
                    {invoice.billing_start_date && (
                        <div className="item">
                            <div className="lh">Billing Period</div>
                            <div className="val" style={{ fontSize: "8.5pt" }}>{formatDate(invoice.billing_start_date)} – {formatDate(invoice.billing_end_date)}</div>
                        </div>
                    )}
                    {/* Iter 42 — commercial references (Proposal / Client PO) for
                        enterprise reconciliation. Each is rendered only when set
                        so manual / non-PO invoices keep a clean meta strip.   */}
                    {invoice.proposal_number && (
                        <div className="item" data-testid="invoice-meta-proposal">
                            <div className="lh">REEDOS Proposal No</div>
                            <div className="val mono">{invoice.proposal_number}</div>
                        </div>
                    )}
                    {invoice.external_po_number && (
                        <div className="item" data-testid="invoice-meta-client-po">
                            <div className="lh">Client PO No.</div>
                            <div className="val mono">{invoice.external_po_number}</div>
                        </div>
                    )}
                    {invoice.external_po_date && (
                        <div className="item" data-testid="invoice-meta-client-po-date">
                            <div className="lh">Client PO Date</div>
                            <div className="val" style={{ fontSize: "8.5pt" }}>{formatDate(invoice.external_po_date)}</div>
                        </div>
                    )}
                    {/* Legacy customer_po_ref preserved for back-compat with
                        invoices created before Iter 41 (external_po_number).  */}
                    {!invoice.external_po_number && invoice.customer_po_ref && (
                        <div className="item">
                            <div className="lh">Customer PO</div>
                            <div className="val mono">{invoice.customer_po_ref}</div>
                        </div>
                    )}
                    {!invoice.billing_start_date && (
                        <div className="item">
                            <div className="lh">Payment Terms</div>
                            <div className="val">{invoice.payment_terms}</div>
                        </div>
                    )}
                </div>

                {/* 01 / Items */}
                <section className="section" data-testid="invoice-items-section">
                    <div className="section-title">
                        {sectionEyebrow("01", "Items & Charges")}
                        <h2>Items &amp; Charges</h2>
                        <div className="accent-rule" />
                    </div>
                    <div className="items-card">
                        {uniformGst ? (
                            /* ────────────────────────────────────────────────
                               Iter 42 — Simplified, enterprise-friendly view.
                               Columns: # · Description · HSN/SAC · Amount.
                               GST broken out ONCE in the footer summary.   */
                            <table className="items" data-testid="invoice-items-table-simplified">
                                <thead>
                                    <tr>
                                        <th className="text-left num-col">#</th>
                                        <th className="text-left">Description</th>
                                        {!suppressHsnColumn && (
                                            <th className="text-left" style={{ width: "22mm" }}>HSN/SAC</th>
                                        )}
                                        <th style={{ width: "32mm" }}>Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {builderMirror ? (
                                        (() => {
                                            /* ────────────────────────────────────────────
                                               Iter 2026-02 · Invoice = Proposal-mirror UAT.
                                               Renders the invoice line items using the same
                                               3-mode logic as ProposalPrint.jsx (Iter 97.15):
                                                 - product_header  → full-width row, name +
                                                                     subtitle, no amount
                                                 - module          → ↳-prefixed name,
                                                                     amount if mode=itemized
                                                 - atomic          → normal row, amount if
                                                                     mode != consolidated
                                               Whatever the client sees on the proposal
                                               they see on the invoice — identical rows,
                                               identical descriptions, identical price-
                                               visibility rules.  */
                                            const {
                                                groups, hasOptions, mode,
                                                showModulePrices, showAtomicPrices, showSubtotals,
                                                perGroupSubtotal,
                                            } = builderMirror;
                                            const rows = [];
                                            let counter = 0;
                                            const formatAmountCell = (val, visible) => {
                                                if (!visible) return "";
                                                return (Number(val) || 0) > 0 ? formatINR(val, invoice?.currency) : "";
                                            };
                                            const colSpanTotal = suppressHsnColumn ? 3 : 4;
                                            for (const [label, lines] of groups.entries()) {
                                                if (hasOptions) {
                                                    rows.push(
                                                        <tr key={`band-${label}`} className="option-band">
                                                            <td colSpan={colSpanTotal} data-testid={`invoice-option-band-${label}`}>
                                                                Product · {label === "__default__" ? "Other items" : label}
                                                            </td>
                                                        </tr>
                                                    );
                                                    const optDesc = (lines.find((l) => (l.option_description || "").trim()) || {}).option_description;
                                                    if (optDesc) {
                                                        rows.push(
                                                            <tr key={`band-desc-${label}`} className="option-band-desc">
                                                                <td colSpan={colSpanTotal} className="option-description">
                                                                    {optDesc}
                                                                </td>
                                                            </tr>
                                                        );
                                                    }
                                                    counter = 0;
                                                }
                                                for (const li of lines) {
                                                    const rk = (li.row_kind || "atomic").toLowerCase();
                                                    const isHeader = rk === "product_header";
                                                    const isModule = rk === "module";
                                                    const showAmount = isModule
                                                        ? showModulePrices
                                                        : isHeader
                                                            ? false     // headers never show a price cell
                                                            : showAtomicPrices;
                                                    if (!isHeader) counter += 1;
                                                    const nameContent = isModule ? (
                                                        <div className="scope-module-line">↳ {li.product_name || li.description}</div>
                                                    ) : (
                                                        <>
                                                            <div className={isHeader ? "product-name" : "desc"}>
                                                                {li.product_name || li.description}
                                                            </div>
                                                            {li.product_subtitle && (
                                                                <div className="product-sub">{li.product_subtitle}</div>
                                                            )}
                                                        </>
                                                    );
                                                    rows.push(
                                                        <tr
                                                            key={li.id}
                                                            className={isModule ? "itemized-module-row" : isHeader ? "itemized-product-header" : ""}
                                                            data-testid={`invoice-item-${li.id}`}
                                                            data-row-kind={rk}
                                                        >
                                                            <td className="text-left num-col">
                                                                {isHeader || isModule ? "" : String(counter).padStart(2, "0")}
                                                            </td>
                                                            <td className="text-left">
                                                                {nameContent}
                                                                {li.is_amc_period && (
                                                                    <>
                                                                        <div className="amc-charge-label" data-testid={`amc-charge-label-${li.id}`}>
                                                                            {li.service_charge_label || "Service Charges"}
                                                                        </div>
                                                                        <div className="amc-period" data-testid={`amc-period-${li.id}`}>
                                                                            <span className="label">Service Period:</span>
                                                                            {formatDate(li.service_period_start)} → {formatDate(li.service_period_end)}
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </td>
                                                            {!suppressHsnColumn && (
                                                                <td className="text-left mono" style={{ fontFamily: "'Satoshi', sans-serif", color: "var(--muted)", fontSize: "8.5pt" }}>
                                                                    {isHeader ? "" : (li.hsn_sac_code || "—")}
                                                                </td>
                                                            )}
                                                            <td className="figure">
                                                                {isHeader ? "" : formatAmountCell(li.amount, showAmount)}
                                                            </td>
                                                        </tr>
                                                    );
                                                }
                                                // Per-group subtotal (mode: itemized / grouped;
                                                // hidden in consolidated). Only render when the
                                                // invoice has more than one group AND the
                                                // subtotal itself is meaningful (> 0).
                                                if (hasOptions && showSubtotals && (perGroupSubtotal.get(label) || 0) > 0) {
                                                    rows.push(
                                                        <tr key={`sub-${label}`} className="option-subtotal">
                                                            <td colSpan={colSpanTotal - 1} style={{ textAlign: "right" }}>
                                                                Subtotal — {label === "__default__" ? "Other items" : label}
                                                            </td>
                                                            <td className="figure">{formatINR(perGroupSubtotal.get(label), invoice?.currency)}</td>
                                                        </tr>
                                                    );
                                                }
                                            }
                                            // Consolidated-mode footnote — no per-line prices
                                            // visible, so tell the reader where the total lives.
                                            if (mode === "consolidated") {
                                                rows.push(
                                                    <tr key="consolidated-note" className="mirror-consolidated-note">
                                                        <td colSpan={colSpanTotal} style={{ fontSize: "8pt", color: "var(--muted)", fontStyle: "italic", padding: "3mm 2mm 0" }}>
                                                            Commercials presented on a consolidated basis — refer to the Grand Total below.
                                                        </td>
                                                    </tr>
                                                );
                                            }
                                            return rows;
                                        })()
                                    ) : (
                                        invoice.line_items.map((li, i) => (
                                        <tr key={li.id}>
                                            <td className="text-left num-col">{String(i + 1).padStart(2, "0")}</td>
                                            <td className="text-left">
                                                <div className="desc">{li.description}</div>
                                                {li.is_amc_period && (
                                                    <>
                                                        <div className="amc-charge-label" data-testid={`amc-charge-label-${li.id}`}>
                                                            {li.service_charge_label || "Service Charges"}
                                                        </div>
                                                        <div className="amc-period" data-testid={`amc-period-${li.id}`}>
                                                            <span className="label">Service Period:</span>
                                                            {formatDate(li.service_period_start)} → {formatDate(li.service_period_end)}
                                                        </div>
                                                    </>
                                                )}
                                            </td>
                                            {!suppressHsnColumn && (
                                                <td className="text-left mono" style={{ fontFamily: "'Satoshi', sans-serif", color: "var(--muted)", fontSize: "8.5pt" }}>{li.hsn_sac_code || "—"}</td>
                                            )}
                                            <td className="figure">{formatINR(li.amount, invoice?.currency)}</td>
                                        </tr>
                                        ))
                                    )}
                                </tbody>
                                <tfoot>
                                    {/* Iter C.2.1.e — I-A Transparent presentation
                                        for invoices billed against a PO with a frozen
                                        Special Offer. Renders Standard → Less: Offer
                                        before the legacy Taxable row. Reason code is
                                        NEVER rendered. Hidden for non-discounted
                                        invoices (legacy preservation). */}
                                    {invoice.special_offer && invoice.special_offer.enabled && (
                                        <>
                                            <tr className="subtotal-row" data-testid="invoice-special-offer-standard">
                                                <td colSpan={suppressHsnColumn ? 2 : 3} className="label">Standard Commercial Value</td>
                                                <td>{formatINR(invoice.special_offer.standard_value_at_apply, invoice?.currency)}</td>
                                            </tr>
                                            <tr className="subtotal-row" data-testid="invoice-special-offer-less" style={{ color: "#8a1d2a" }}>
                                                <td colSpan={suppressHsnColumn ? 2 : 3} className="label" style={{ color: "#8a1d2a" }}>Less: {invoice.special_offer.label}</td>
                                                <td>−{formatINR(invoice.special_offer.discount_amount, invoice?.currency)}</td>
                                            </tr>
                                        </>
                                    )}
                                    <tr className="subtotal-row">
                                        <td colSpan={suppressHsnColumn ? 2 : 3} className="label">{invoice.special_offer?.enabled ? "Taxable Value" : "Subtotal"}</td>
                                        <td>{formatINR(invoice.subtotal, invoice?.currency)}</td>
                                    </tr>
                                    {/* Iter 43: GST rows render only when the invoice is tax-applicable.
                                        International / zero-rated invoices show a clean
                                        Subtotal → Grand Total flow. */}
                                    {gstApplicable && isIntra && (
                                        <>
                                            <tr className="subtotal-row">
                                                <td colSpan={suppressHsnColumn ? 2 : 3} className="label">CGST {invoice.line_items[0]?.gst_percent / 2}%</td>
                                                <td>{formatINR(invoice.gst_breakup.cgst, invoice?.currency)}</td>
                                            </tr>
                                            <tr className="subtotal-row">
                                                <td colSpan={suppressHsnColumn ? 2 : 3} className="label">SGST {invoice.line_items[0]?.gst_percent / 2}%</td>
                                                <td>{formatINR(invoice.gst_breakup.sgst, invoice?.currency)}</td>
                                            </tr>
                                        </>
                                    )}
                                    {gstApplicable && !isIntra && (
                                        <tr className="subtotal-row">
                                            <td colSpan={suppressHsnColumn ? 2 : 3} className="label">IGST {invoice.line_items[0]?.gst_percent}%</td>
                                            <td>{formatINR(invoice.gst_breakup.igst, invoice?.currency)}</td>
                                        </tr>
                                    )}
                                    <tr className="grand-row">
                                        <td colSpan={suppressHsnColumn ? 2 : 3} className="label">Grand Total</td>
                                        <td>{formatINR(invoice.total_amount, invoice?.currency)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        ) : (
                            /* ────────────────────────────────────────────────
                               Detailed view — fallback for mixed-GST,
                               exempt / export / reverse-charge invoices.    */
                            <table className="items" data-testid="invoice-items-table-detailed">
                                <thead>
                                    <tr>
                                        <th className="text-left num-col">#</th>
                                        <th className="text-left">Description</th>
                                        {!suppressHsnColumn && (
                                            <th className="text-left" style={{ width: "16mm" }}>HSN/SAC</th>
                                        )}
                                        <th style={{ width: "11mm" }}>Qty</th>
                                        <th style={{ width: "20mm" }}>Rate</th>
                                        <th style={{ width: "22mm" }}>Amount</th>
                                        {isIntra ? (
                                            <>
                                                <th style={{ width: "18mm" }}>CGST</th>
                                                <th style={{ width: "18mm" }}>SGST</th>
                                            </>
                                        ) : (
                                            <th style={{ width: "20mm" }}>IGST</th>
                                        )}
                                        <th style={{ width: "23mm" }}>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoice.line_items.map((li, i) => {
                                        const split = isIntra
                                            ? { cgst: li.gst_amount / 2, sgst: li.gst_amount / 2 }
                                            : { igst: li.gst_amount };
                                        const qty = li.quantity || 1;
                                        return (
                                            <tr key={li.id}>
                                                <td className="text-left num-col">{String(i + 1).padStart(2, "0")}</td>
                                                <td className="text-left">
                                                    <div className="desc">{li.description}</div>
                                                    {li.is_amc_period && (
                                                        <>
                                                            <div className="amc-charge-label" data-testid={`amc-charge-label-${li.id}`}>
                                                                {li.service_charge_label || "Service Charges"}
                                                            </div>
                                                            <div className="amc-period" data-testid={`amc-period-${li.id}`}>
                                                                <span className="label">Service Period:</span>
                                                                {formatDate(li.service_period_start)} → {formatDate(li.service_period_end)}
                                                            </div>
                                                        </>
                                                    )}
                                                </td>
                                                {!suppressHsnColumn && (
                                                    <td className="text-left mono" style={{ fontFamily: "'Satoshi', sans-serif", color: "var(--muted)", fontSize: "8.5pt" }}>{li.hsn_sac_code || "—"}</td>
                                                )}
                                                <td className="figure">{qty}</td>
                                                <td className="figure">{formatINR(li.amount / qty, invoice?.currency)}</td>
                                                <td className="figure">{formatINR(li.amount, invoice?.currency)}</td>
                                                {isIntra ? (
                                                    <>
                                                        <td className="figure">{formatINR(split.cgst, invoice?.currency)}<span className="gst-pct">{li.gst_percent / 2}%</span></td>
                                                        <td className="figure">{formatINR(split.sgst, invoice?.currency)}<span className="gst-pct">{li.gst_percent / 2}%</span></td>
                                                    </>
                                                ) : (
                                                    <td className="figure">{formatINR(split.igst, invoice?.currency)}<span className="gst-pct">{li.gst_percent}%</span></td>
                                                )}
                                                <td className="figure figure-em">{formatINR(li.total_amount, invoice?.currency)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    {invoice.special_offer && invoice.special_offer.enabled && (
                                        <>
                                            <tr className="subtotal-row" data-testid="invoice-special-offer-standard-detailed">
                                                <td colSpan={(isIntra ? 6 : 5) - (suppressHsnColumn ? 1 : 0)} className="label">Standard Commercial Value</td>
                                                <td className="figure-em">{formatINR(invoice.special_offer.standard_value_at_apply, invoice?.currency)}</td>
                                            </tr>
                                            <tr className="subtotal-row" data-testid="invoice-special-offer-less-detailed" style={{ color: "#8a1d2a" }}>
                                                <td colSpan={(isIntra ? 6 : 5) - (suppressHsnColumn ? 1 : 0)} className="label" style={{ color: "#8a1d2a" }}>Less: {invoice.special_offer.label}</td>
                                                <td className="figure-em" style={{ color: "#8a1d2a" }}>−{formatINR(invoice.special_offer.discount_amount, invoice?.currency)}</td>
                                            </tr>
                                        </>
                                    )}
                                    <tr className="subtotal-row">
                                        <td colSpan={5 - (suppressHsnColumn ? 1 : 0)} className="label">{invoice.special_offer?.enabled ? "Taxable Value" : "Subtotal"}</td>
                                        <td>{formatINR(invoice.subtotal, invoice?.currency)}</td>
                                        {isIntra ? (
                                            <>
                                                <td>{formatINR(invoice.gst_breakup.cgst, invoice?.currency)}</td>
                                                <td>{formatINR(invoice.gst_breakup.sgst, invoice?.currency)}</td>
                                            </>
                                        ) : (
                                            <td>{formatINR(invoice.gst_breakup.igst, invoice?.currency)}</td>
                                        )}
                                        <td className="figure-em">{formatINR(invoice.total_amount, invoice?.currency)}</td>
                                    </tr>
                                    <tr className="grand-row">
                                        <td colSpan={(isIntra ? 6 : 5) - (suppressHsnColumn ? 1 : 0)} className="label">Grand Total</td>
                                        <td colSpan={3}>{formatINR(invoice.total_amount, invoice?.currency)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        )}
                    </div>
                    {invoice.amount_in_words && (
                        <div className="words-callout"><span className="lh">Amount in Words</span>{invoice.amount_in_words}</div>
                    )}
                </section>

                {/* 02 / Payment Instructions + Signatory */}
                <section className="section" data-testid="invoice-payment-section">
                    <div className="section-title">
                        {sectionEyebrow("02", "Payment Instructions")}
                        <h2>Payment Instructions</h2>
                        <div className="accent-rule" />
                    </div>
                    <div className="payment-grid">
                        <div className="payment-card">
                            <div className="head">Remit to · Cheque/DD in favour of {company.bank?.account_holder || company.legal_name}</div>
                            <div className="row"><div className="lh">Bank</div><div className="val">{company.bank.name}</div></div>
                            <div className="row"><div className="lh">A/c No.</div><div className="val mono">{company.bank.account_no}</div></div>
                            <div className="row"><div className="lh">IFSC</div><div className="val mono">{company.bank.ifsc}</div></div>
                            <div className="row"><div className="lh">MICR</div><div className="val mono">{company.bank.micr}</div></div>
                            <div className="note">Please mention <strong>{invoice.invoice_number}</strong> in the payment reference. Receipts are confirmed via email within 1 business day of clearance.</div>
                        </div>
                    </div>
                </section>

                {/* 03 / Notes & Declarations */}
                {Array.isArray(company.footer_declarations) && company.footer_declarations.length > 0 && (
                    <section className="section" data-testid="invoice-declarations-section">
                        <div className="section-title">
                            {sectionEyebrow("03", "Notes & Declarations")}
                            <h2>Notes &amp; Declarations</h2>
                            <div className="accent-rule" />
                        </div>
                        <ol className="declarations">
                            {company.footer_declarations.map((d, i) => <li key={i}>{d}</li>)}
                        </ol>
                        {invoice.notes && (
                            <div style={{ marginTop: "5mm", fontSize: "9pt", color: "var(--ink)" }}>
                                <strong style={{ color: "var(--reedos-blue)", letterSpacing: "0.1em", textTransform: "uppercase", fontSize: "8pt", marginRight: "6px" }}>Notes</strong>
                                {invoice.notes}
                            </div>
                        )}
                    </section>
                )}

                {/* Footer */}
                <div className="doc-footer">
                    <div className="org-line"><strong>{company.legal_name}</strong> · GSTIN {company.gstin}</div>
                    <div className="conf">REEDOS · Tax Invoice</div>
                </div>
            </div>
        </>
    );
}

/* ────────────────────────────────────────────────────────────────────
   Iter 2026-02 · Route-based InvoicePrint page. Wraps `InvoicePrintBody`
   with a sticky top nav (Back + Print / Save as PDF) and the
   `min-h-screen` background that only makes sense when the page is
   rendered stand-alone at /invoices/:id/print.
   ──────────────────────────────────────────────────────────────────── */
export default function InvoicePrint() {
    const { id } = useParams();
    const [invoice, setInvoice] = useState(null);
    const [company, setCompany] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [inv, ci] = await Promise.all([
                    api.get(`/billing/invoice/${id}`),
                    api.get("/billing/company-info"),
                ]);
                setInvoice(inv.data);
                setCompany(ci.data);
            } catch (err) {
                toast.error(formatApiError(err, "Failed to load invoice"));
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    useEffect(() => {
        const prev = document.title;
        if (invoice?.invoice_number) {
            document.title = `REEDOS Invoice — ${invoice.invoice_number}`;
        }
        return () => { document.title = prev; };
    }, [invoice?.invoice_number]);

    if (loading) return <div className="p-10 text-sm text-slate-500">Loading…</div>;
    if (!invoice || !company) return <div className="p-10">Invoice not found</div>;

    return (
        <div className="min-h-screen bg-slate-100" data-testid="invoice-print-page">
            <div className="no-print bg-white border-b border-slate-200 px-6 py-3 sticky top-0 z-10 flex items-center justify-between">
                <button onClick={() => window.history.back()} className="text-sm text-slate-700 hover:text-slate-900 inline-flex items-center gap-1.5">
                    <ArrowLeft size={14} /> Back
                </button>
                <Button onClick={() => window.print()} data-testid="print-invoice-btn" className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5">
                    <Printer size={16} weight="fill" /> Print / Save as PDF
                </Button>
            </div>
            <InvoicePrintBody invoice={invoice} company={company} />
        </div>
    );
}
